import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { exchangeCode, getMe } from '@/api/auth';
import { needsProfile } from '@/lib/onboarding-profile';
import { buildWelcomeUrl, getPostAuthRedirect } from '@/lib/auth-intent';
import { Loader2 } from 'lucide-react';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth, clearAuth } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true });
        return;
      }

      if (!code) {
        navigate('/login?error=missing_code', { replace: true });
        return;
      }

      try {
        const tokens = await exchangeCode(code);
        localStorage.setItem('access_token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);

        const user = await getMe();
        setAuth(user, tokens.access_token, tokens.refresh_token);
        // OAuth can't tell signup from login on the FE; rely on the server flag.
        // Honor a forwarded post-auth redirect (e.g. an AWS Marketplace ?ref= link).
        navigate(
          needsProfile(user) ? buildWelcomeUrl(searchParams) : getPostAuthRedirect(searchParams),
          { replace: true },
        );
      } catch {
        clearAuth();
        navigate('/login?error=auth_failed', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, navigate, setAuth, clearAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm text-center space-y-6 animate-fade-in">
        <div className="flex justify-center">
          <span className="text-2xl font-semibold tracking-tight text-foreground">gateco</span>
        </div>
        <div className="rounded-lg border bg-card p-8 space-y-4 shadow-elevation">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">Completing sign in</p>
            <p className="text-sm text-muted-foreground">Please wait while we verify your credentials…</p>
          </div>
          <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-shimmer w-1/2" />
          </div>
        </div>
      </div>
    </div>
  );
}
